/**
 * The kinds of feedback a reader can submit on a doc page.
 *
 * @public
 */
export const DOC_FEEDBACK_CATEGORIES = ['question', 'concern', 'inaccuracy', 'suggestion'] as const;

/** @public */
export type DocFeedbackCategory = (typeof DOC_FEEDBACK_CATEGORIES)[number];

/**
 * Triage statuses of a feedback item.
 *
 * @public
 */
export const DOC_FEEDBACK_STATUSES = ['new', 'in_progress', 'resolved', 'wont_fix'] as const;

/** @public */
export type DocFeedbackStatus = (typeof DOC_FEEDBACK_STATUSES)[number];

/**
 * Statuses that are considered "open" (still needing attention).
 *
 * @public
 */
export const DOC_FEEDBACK_OPEN_STATUSES: DocFeedbackStatus[] = ['new', 'in_progress'];

/** @public */
export const DOC_FEEDBACK_CATEGORY_LABELS: Record<DocFeedbackCategory, string> = {
  question: 'Question',
  concern: 'Concern',
  inaccuracy: 'Inaccuracy',
  suggestion: 'Suggestion',
};

/** @public */
export const DOC_FEEDBACK_STATUS_LABELS: Record<DocFeedbackStatus, string> = {
  new: 'New',
  in_progress: 'In progress',
  resolved: 'Resolved',
  wont_fix: "Won't fix",
};

/** Maximum lengths enforced by the backend. @public */
export const DOC_FEEDBACK_LIMITS = {
  message: 5000,
  response: 5000,
  selectedText: 2000,
  pageTitle: 300,
  pagePath: 1000,
} as const;

/**
 * A feedback item submitted on a doc page.
 *
 * @public
 */
export type DocFeedback = {
  id: string;
  /** Lower-cased entity ref of the documented entity, e.g. `component:default/my-service`. */
  entityRef: string;
  /** Lower-cased entity ref of the owner (`spec.owner`) at submission time, if any. */
  ownerRef?: string;
  /** Path of the page within the docs site, without leading/trailing slashes. Empty for the index page. */
  pagePath: string;
  pageTitle?: string;
  category: DocFeedbackCategory;
  message: string;
  /** Text the reader highlighted on the page when submitting. */
  selectedText?: string;
  /** User entity ref of the submitter. */
  submittedBy: string;
  status: DocFeedbackStatus;
  /** Reply from the owning team / Platform Engineering. */
  response?: string;
  respondedBy?: string;
  /** ISO 8601 timestamp. */
  createdAt: string;
  /** ISO 8601 timestamp. */
  updatedAt: string;
};

/** @public */
export type DocFeedbackList = {
  items: DocFeedback[];
  totalCount: number;
};

/**
 * Which feedback to list.
 * - `owned`: feedback on docs owned by the current user or their groups
 * - `all`: all feedback (requires {@link docFeedbackAdminPermission})
 * - `mine`: feedback submitted by the current user
 *
 * @public
 */
export type DocFeedbackScope = 'owned' | 'all' | 'mine';

/** @public */
export type DocFeedbackListRequest = {
  scope?: DocFeedbackScope;
  status?: DocFeedbackStatus[];
  category?: DocFeedbackCategory[];
  entityRef?: string;
  ownerRef?: string;
  limit?: number;
  offset?: number;
};

/** Body of `POST /feedback`. @public */
export type CreateDocFeedbackRequest = {
  entityRef: string;
  pagePath: string;
  pageTitle?: string;
  category: DocFeedbackCategory;
  message: string;
  selectedText?: string;
};

/** Body of `PATCH /feedback/:id`. @public */
export type UpdateDocFeedbackRequest = {
  status?: DocFeedbackStatus;
  response?: string;
};

/** Body of `POST /ratings`. @public */
export type DocRatingRequest = {
  entityRef: string;
  pagePath: string;
  helpful: boolean;
};

/** "Was this page helpful?" aggregate. @public */
export type DocRatingSummary = {
  entityRef: string;
  /** Present when the summary is for a single page. */
  pagePath?: string;
  helpful: number;
  notHelpful: number;
  total: number;
  /** Percentage (0-100) of helpful votes, or undefined when there are no votes. */
  helpfulPercent?: number;
  /** The current user's vote on the page, when the summary is for a single page. */
  userVote?: boolean;
};

/** Response of `GET /summary`. @public */
export type DocFeedbackEntitySummary = {
  entityRef: string;
  openCount: number;
  totalCount: number;
};

/**
 * Which page ratings to list.
 * - `owned`: pages of docs owned by the current user or their groups
 * - `all`: every page (requires {@link docFeedbackAdminPermission})
 *
 * @public
 */
export type DocPageRatingScope = 'owned' | 'all';

/** Query of `GET /ratings`. @public */
export type DocPageRatingsRequest = {
  scope?: DocPageRatingScope;
  entityRef?: string;
  ownerRef?: string;
};

/** "Was this page helpful?" votes of one page. @public */
export type DocPageRating = {
  entityRef: string;
  ownerRef?: string;
  pagePath: string;
  helpful: number;
  notHelpful: number;
  total: number;
  /** Percentage (0-100) of helpful votes. */
  helpfulPercent: number;
  /** ISO 8601 timestamp of the latest vote. */
  lastVotedAt: string;
};

/** Response of `GET /ratings`. @public */
export type DocPageRatingList = {
  items: DocPageRating[];
};

/**
 * Response of `GET /ratings/top`: docs with the most 👍 votes, best first.
 * Open to every signed-in user, so it carries the ranking only, never the counts.
 * @public
 */
export type TopLikedDocList = {
  items: Array<{ entityRef: string }>;
};

/**
 * Open feedback that is new for the signed-in user: on docs they own (all docs for admins),
 * submitted by someone else since they last opened the Doc Feedback page. Drives the sidebar badge.
 *
 * @public
 */
export type DocFeedbackUnseenCount = {
  count: number;
  /** When the user last opened the Doc Feedback page; absent if they never did. */
  lastSeenAt?: string;
};
